// Import the Angular Component decorator and OnInit lifecycle interface.
import { Component, OnInit } from '@angular/core';

// Define the Angular component metadata for the Breaking the Cycle community services page.
@Component({
  // Register the HTML selector used for this component.
  selector: 'app-breaking-the-cycle',
  // Link the component to its HTML template file.
  templateUrl: './breaking-the-cycle.component.html',
  // Link the component to its component-specific SCSS file.
  styleUrls: ['./breaking-the-cycle.component.scss']
})
// Export the Breaking the Cycle component and use the standard OnInit structure used by the project.
export class BreakingTheCycleComponent implements OnInit {
  // Store the four key facts shown directly below the hero section.
  readonly facts = [
    // Display the free-program fact with its matching Bootstrap icon.
    ['bi-cash-stack', 'Free', 'No cost to you, ever'],
    // Display the program duration fact with its matching Bootstrap icon.
    ['bi-calendar3', '16 weeks', 'Weekly sessions'],
    // Display the small-group size fact with its matching Bootstrap icon.
    ['bi-people', 'Small group', '4 to 8 men'],
    // Display the Darwin commencement fact with its matching Bootstrap icon.
    ['bi-geo-alt', 'Darwin, NT', 'Starting October 2026']
  ];

  // Store the four stages that make up the complete 16-week participant journey.
  readonly journey = [
    // Define the Establish stage covering Weeks 1 to 4.
    ['01', 'Weeks 1–4', 'Establish', 'You set up the circle together. We look at what power and control mean in relationships and what genuine accountability looks like.'],
    // Define the Build stage covering Weeks 5 to 8.
    ['02', 'Weeks 5–8', 'Build', 'Healthy masculinity. Emotional regulation. What a healthy relationship really looks like. At Week 8, your progress is measured.'],
    // Define the Deepen stage covering Weeks 9 to 12.
    ['03', 'Weeks 9–12', 'Deepen', 'Communication. Conflict without harm. Cultural identity and strength. Week 12 — on Country with Elders.'],
    // Define the Sustain stage covering Weeks 13 to 16.
    ['04', 'Weeks 13–16', 'Sustain', 'Good Lives goals. Your Staying Strong Plan. Week 16 — graduation, certificate, and your next step confirmed.']
  ];

  // Store the five steps that explain what a participant can expect on their first day.
  readonly firstDay = [
    // Define the welcome step.
    ['1', 'Welcome', 'Your case manager meets you. Tea, water, no rush. You can bring a family member or Elder.'],
    // Define the participant-rights step.
    ['2', 'Your Rights', 'We explain the program, your privacy, and your right to leave at any time.'],
    // Define the confidential assessment step.
    ['3', 'Assessment', 'A short, confidential questionnaire. No right or wrong answers. Just honest.'],
    // Define the personal case-plan step.
    ['4', 'Your Case Plan', 'You and your case manager build your personal plan together. Your goals, your words. You keep a copy.'],
    // Define the first-session confirmation step.
    ['5', 'Your First Session', 'Time, location, and your case manager\'s number confirmed. You are ready to go.']
  ];

  // Store the education and employment pathways available to program participants.
  readonly pathways = [
    // Display access to White Card training from Week 1.
    'White Card (Construction Induction) — available from Week 1',
    // Display driver licence preparation support.
    'Driver Licence preparation — supported in your case plan',
    // Display the nationally recognised Certificate II pathways.
    'Certificate II in Civil Construction, Resources and Infrastructure, or Hospitality — nationally recognised',
    // Display the traineeship and apprenticeship pathway through ECB's GTO model.
    'Traineeship or Apprenticeship — ECB places you with an employer (GTO model)',
    // Display the warm employment-referral pathway to Darwin employers.
    'Employment Referral — warm introduction to Darwin employers when you are ready'
  ];

  // Store the support roles that participants can access throughout the program.
  readonly support = [
    // Describe the Case Manager role.
    ['Case Manager', 'Your main person throughout the program. Builds your plan, meets with you regularly, and supports you with practical needs.'],
    // Describe the Program Facilitator role.
    ['Program Facilitator', 'Leads your yarning circle each week. First Nations male facilitator. Present every session.'],
    // Describe the Elder or Cultural Mentor role.
    ['Elder / Cultural Mentor', 'Cultural supervision and on-country connection throughout the program.'],
    // Describe the Senior Practitioner role.
    ['Senior Practitioner', 'Clinical oversight and extra support whenever you need it.']
  ];

  // Store the supported referral pathways displayed in the referrer section.
  readonly referrals = [
    // Display the NT Department of Corrections referral pathway.
    'NT Department of Corrections — warm referral at point of justice contact',
    // Display the community or family referral pathway.
    'Community or Family — an Elder, family member, or community connector can bring a young man forward',
    // Display the direct self-referral pathway.
    'Self-Referral — young man contacts ECB directly by phone or in person',
    // Display the Darwin Schools referral pathway.
    'Darwin Schools — school wellbeing staff can refer students directly',
    // Display the Territory Families and NT Health referral pathway.
    'Territory Families / NT Health — warm accompanied referral under information sharing arrangements'
  ];

  // Keep page initialisation in a dedicated method to match the existing component structure used in the project.
  loadPageData(): void {
    // This page currently uses static content from the approved brief, so no API call is required here.
  }

  // Run the component initialisation logic when Angular creates the page.
  ngOnInit(): void {
    // Load any data required by the page.
    this.loadPageData();
  }
}
